import React from 'react';
import { Box, IconButton, Icon } from '@mui/material';
import { ReactComponent as EditIcon } from 'assets/edit-icon.svg';
import { ReactComponent as ExternalLinkArrow } from 'assets/ExternalLinkArrow.svg';
import MobileTypography from './MobileTypography';
import { replayChrome } from 'sentryReplayPrivacy';

/**
 * MobileSettingsComponents - Reusable components for mobile settings pages
 * Centralized components to improve DRY principles and consistency
 */

// Reusable EditIconButton component
export const EditIconButton = ({
  onClick,
  ariaLabel,
  size = 'medium',
}: {
  onClick: () => void;
  ariaLabel: string;
  size?: 'small' | 'medium' | 'large';
}) => (
  <IconButton size={size} onClick={onClick} aria-label={ariaLabel}>
    <EditIcon />
  </IconButton>
);

// Reusable PasswordVisibilityToggle component
export const PasswordVisibilityToggle = ({
  showPassword,
  onToggle,
}: {
  showPassword: boolean;
  onToggle: () => void;
}) => (
  <IconButton aria-label="toggle password visibility" onClick={onToggle} edge="end" size="large">
    <Icon className="text-20" color="action">
      {showPassword ? 'visibility' : 'visibility_off'}
    </Icon>
  </IconButton>
);

// Section Header component
export const SectionHeader = ({
  label,
  onEdit,
  isEditing,
  editAriaLabel,
  isEditable = true,
}: {
  label: string;
  onEdit: () => void;
  isEditing: boolean;
  editAriaLabel: string;
  isEditable?: boolean;
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <MobileTypography variant="label" {...replayChrome}>
      {label}
    </MobileTypography>
    {!isEditing && isEditable && <EditIconButton onClick={onEdit} ariaLabel={editAriaLabel} />}
  </Box>
);

// Menu Item Button component
export const MenuItemButton = ({
  item,
  onClick,
}: {
  item: { id: string; label: string; icon: React.ComponentType };
  onClick: () => void;
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      px: 3,
      py: 2,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'action.hover',
      },
    }}
    onClick={onClick}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <item.icon />
      <MobileTypography variant="body">{item.label}</MobileTypography>
    </Box>
  </Box>
);

// External Link component
export const ExternalLink = ({
  children,
  href,
  ...props
}: {
  children: React.ReactNode;
  href: string;
  [key: string]: unknown;
}) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      mt: 2,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    }}
    {...props}
  >
    <MobileTypography
      variant="title"
      sx={{
        color: '#007EFF',
        fontWeight: 400,
      }}
    >
      {children}
    </MobileTypography>
    <ExternalLinkArrow style={{ width: '17px', height: '17px' }} />
  </Box>
);
